/* Module Marchand de biens (MDB) — V1 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import KpiCard from "@/components/common/KpiCard";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, Home, TrendingUp, Wallet, Sparkles, CheckCircle2,
  Circle, ChevronRight,
} from "lucide-react";

const STATUS_LABEL = {
  prospection: "Prospection", compromis: "Compromis", acquisition: "Acquisition",
  travaux: "Travaux", commercialisation: "Commercialisation",
  vente: "Vente", termine: "Terminé",
};
const STATUS_TONE = {
  prospection: "info", compromis: "warning", acquisition: "warning",
  travaux: "warning", commercialisation: "info", vente: "success",
  termine: "success",
};

const eur = (v) => `${Number(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;

export default function MdbPage() {
  const [items, setItems] = useState([]);
  const [dash, setDash] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const [l, d] = await Promise.all([
      api.get("/mdb"), api.get("/mdb/dashboard/overview"),
    ]);
    setItems(l.data.items); setDash(d.data);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="animate-fade-up" data-testid="mdb-page">
      <PageHeader
        overline="Marchand de biens"
        title="Projets d&apos;achat / revente"
        description="Gérez vos projets flip : coût total, marge brute prévisionnelle et trésorerie nécessaire calculés automatiquement."
        actions={<Button className="btn-pill" onClick={() => setOpenNew(true)} data-testid="mdb-new-btn">
          <Plus className="h-3 w-3 mr-1" />Nouveau projet
        </Button>}
      />

      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <KpiCard name="mdb-count" label="Projets actifs" value={dash.count_active} icon={Home} tone="accent" />
          <KpiCard name="mdb-total" label="Total projets" value={dash.count_total} />
          <KpiCard name="mdb-marge" label="Marge prévisionnelle" value={eur(dash.total_marge_previsionnelle)} icon={TrendingUp} tone={dash.total_marge_previsionnelle >= 0 ? "success" : "danger"} />
          <KpiCard name="mdb-treso" label="Trésorerie nécessaire" value={eur(dash.total_tresorerie_necessaire)} icon={Wallet} />
        </div>
      )}

      {items.length === 0 && (
        <div className="card-elevated p-12 text-center text-muted-foreground">
          <Home className="mx-auto h-8 w-8 opacity-60 mb-2" />
          Aucun projet. Cliquez sur « Nouveau projet ».
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((p) => (
          <button key={p.id} onClick={() => setDetail(p.id)}
                   className="card-elevated p-5 text-left hover:shadow-lg transition-shadow"
                   data-testid={`mdb-card-${p.id}`}>
            <div className="flex justify-between items-start">
              <div className="min-w-0">
                <div className="font-heading text-base truncate">{p.name}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {p.address?.city || p.address?.line1 || "—"}
                </div>
              </div>
              <StatusBadge tone={STATUS_TONE[p.status]}>{STATUS_LABEL[p.status]}</StatusBadge>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <div>
                <div className="text-[10px] uppercase text-muted-foreground">Coût total</div>
                <div className="font-mono">{eur(p.financials_computed.cout_total)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-muted-foreground">Marge brute</div>
                <div className={`font-mono font-semibold ${p.financials_computed.marge_brute_previsionnelle >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                  {eur(p.financials_computed.marge_brute_previsionnelle)}
                  <span className="text-xs ml-1">({p.financials_computed.marge_brute_pct.toFixed(1)}%)</span>
                </div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase text-muted-foreground">Trésorerie</div>
                <div className="font-mono">{eur(p.financials_computed.tresorerie_necessaire)}</div>
              </div>
            </div>
            <div className="mt-3 flex items-center text-xs text-primary">
              Voir détail<ChevronRight className="h-3 w-3 ml-1" />
            </div>
          </button>
        ))}
      </div>

      {openNew && <ProjectForm onClose={() => setOpenNew(false)} onSaved={() => { setOpenNew(false); load(); }} />}
      {detail && <ProjectDetail projectId={detail} onClose={() => { setDetail(null); load(); }} />}
    </div>
  );
}

function ProjectForm({ onClose, onSaved, existing }) {
  const [form, setForm] = useState(existing || {
    name: "", status: "prospection", company_id: "",
    address: { line1: "", city: "", zipcode: "" },
    financials: {
      prix_achat: 0, frais_notaire: 0, frais_agence: 0,
      cout_travaux_estime: 0, honoraires: 0, autres_frais: 0,
      financement: 0, prix_revente_estime: 0,
    },
    notes: "",
  });
  const [companies, setCompanies] = useState([]);
  useEffect(() => {
    api.get("/companies").then(({ data }) => setCompanies(data.items || []));
  }, []);

  const setFin = (k, v) => setForm((f) => ({ ...f, financials: { ...f.financials, [k]: parseFloat(v) || 0 } }));

  const compute = () => {
    const f = form.financials;
    const cout = (f.prix_achat || 0) + (f.frais_notaire || 0) + (f.frais_agence || 0) + (f.cout_travaux_estime || 0) + (f.honoraires || 0) + (f.autres_frais || 0);
    const marge = (f.prix_revente_estime || 0) - cout;
    const treso = cout - (f.financement || 0);
    return { cout, marge, treso, pct: cout > 0 ? (marge / cout * 100) : 0 };
  };
  const r = compute();

  const submit = async () => {
    if (!form.name) { toast.error("Nom requis"); return; }
    try {
      const payload = {
        name: form.name, status: form.status,
        company_id: form.company_id || null,
        address: form.address, financials: form.financials, notes: form.notes,
      };
      if (existing) {
        await api.patch(`/mdb/${existing.id}`, payload);
        toast.success("Projet mis à jour");
      } else {
        await api.post("/mdb", payload);
        toast.success("Projet créé");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{existing ? "Modifier le projet" : "Nouveau projet MDB"}</DialogTitle>
          <DialogDescription>Les coûts totaux, marge et trésorerie sont calculés automatiquement.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nom *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="mdb-form-name" />
            </div>
            <div>
              <Label>Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="mdb-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Société</Label>
              <Select value={form.company_id || "__none"}
                       onValueChange={(v) => setForm({ ...form, company_id: v === "__none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">—</SelectItem>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.denomination || c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Adresse</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input placeholder="Rue" value={form.address?.line1 || ""}
                       onChange={(e) => setForm({ ...form, address: { ...form.address, line1: e.target.value } })} />
                <Input placeholder="CP" value={form.address?.zipcode || ""}
                       onChange={(e) => setForm({ ...form, address: { ...form.address, zipcode: e.target.value } })} />
                <Input placeholder="Ville" value={form.address?.city || ""}
                       onChange={(e) => setForm({ ...form, address: { ...form.address, city: e.target.value } })} />
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Éléments financiers</div>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["prix_achat", "Prix d&apos;achat"],
                ["frais_notaire", "Frais de notaire"],
                ["frais_agence", "Frais d&apos;agence"],
                ["cout_travaux_estime", "Coût travaux estimé"],
                ["honoraires", "Honoraires"],
                ["autres_frais", "Autres frais"],
                ["financement", "Financement"],
                ["prix_revente_estime", "Prix de revente estimé"],
              ].map(([k, label]) => (
                <div key={k}>
                  <Label>{label} (EUR)</Label>
                  <Input type="number" value={form.financials[k]} onChange={(e) => setFin(k, e.target.value)}
                         data-testid={`mdb-fin-${k}`} />
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="card-elevated p-3 text-center">
              <div className="text-[10px] uppercase text-muted-foreground">Coût total</div>
              <div className="font-mono font-semibold" data-testid="mdb-preview-cout">{eur(r.cout)}</div>
            </div>
            <div className="card-elevated p-3 text-center">
              <div className="text-[10px] uppercase text-muted-foreground">Marge prévisionnelle</div>
              <div className={`font-mono font-semibold ${r.marge >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="mdb-preview-marge">
                {eur(r.marge)} <span className="text-xs">({r.pct.toFixed(1)}%)</span>
              </div>
            </div>
            <div className="card-elevated p-3 text-center">
              <div className="text-[10px] uppercase text-muted-foreground">Trésorerie nécessaire</div>
              <div className="font-mono font-semibold" data-testid="mdb-preview-treso">{eur(r.treso)}</div>
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} data-testid="mdb-form-submit">{existing ? "Enregistrer" : "Créer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProjectDetail({ projectId, onClose }) {
  const [data, setData] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/mdb/${projectId}`);
    setData(data);
  };
  useEffect(() => { load(); }, [projectId]);

  const remove = async () => {
    if (!window.confirm("Supprimer ce projet ?")) return;
    await api.delete(`/mdb/${projectId}`);
    toast.success("Supprimé");
    onClose();
  };

  const toggleStep = async (step_code, done) => {
    await api.patch(`/mdb/${projectId}/timeline`, { step_code, done, actual_date: done ? new Date().toISOString().slice(0, 10) : null });
    load();
  };

  if (!data) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent><div className="p-8 text-center text-muted-foreground">Chargement…</div></DialogContent>
      </Dialog>
    );
  }

  const f = data.financials_computed;

  return (
    <Dialog open={!openEdit} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {data.name}
            <StatusBadge tone={STATUS_TONE[data.status]}>{STATUS_LABEL[data.status]}</StatusBadge>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="card-elevated p-3">
              <div className="text-[10px] uppercase text-muted-foreground">Prix achat</div>
              <div className="font-mono">{eur(f.prix_achat)}</div>
            </div>
            <div className="card-elevated p-3">
              <div className="text-[10px] uppercase text-muted-foreground">Coût total</div>
              <div className="font-mono font-semibold">{eur(f.cout_total)}</div>
            </div>
            <div className="card-elevated p-3">
              <div className="text-[10px] uppercase text-muted-foreground">Prix revente</div>
              <div className="font-mono">{eur(f.prix_revente_estime)}</div>
            </div>
            <div className="card-elevated p-3">
              <div className="text-[10px] uppercase text-muted-foreground">Marge brute</div>
              <div className={`font-mono font-semibold ${f.marge_brute_previsionnelle >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                {eur(f.marge_brute_previsionnelle)} <span className="text-xs">({f.marge_brute_pct.toFixed(1)}%)</span>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="card-elevated p-5">
            <div className="font-heading text-base mb-3">Planning</div>
            <div className="space-y-2">
              {(data.timeline_steps || []).map((s) => (
                <div key={s.step_code} className="flex items-center gap-3 p-2 rounded hover:bg-secondary/40"
                      data-testid={`mdb-step-${s.step_code}`}>
                  <button onClick={() => toggleStep(s.step_code, !s.done)}
                           data-testid={`mdb-step-toggle-${s.step_code}`}>
                    {s.done
                      ? <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                      : <Circle className="h-5 w-5 text-muted-foreground" />}
                  </button>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{s.step_label}</div>
                    {s.actual_date && <div className="text-xs text-muted-foreground">Fait le {s.actual_date}</div>}
                    {!s.actual_date && s.planned_date && <div className="text-xs text-muted-foreground">Prévu : {s.planned_date}</div>}
                  </div>
                  <Input type="date"
                         className="w-40"
                         value={s.planned_date || ""}
                         onChange={(e) => api.patch(`/mdb/${projectId}/timeline`, {
                           step_code: s.step_code, planned_date: e.target.value,
                         }).then(load)} />
                </div>
              ))}
            </div>
          </div>

          {/* Documents liés */}
          <div className="card-elevated p-5">
            <div className="font-heading text-base mb-2">Documents liés</div>
            {(data.linked_documents || []).length === 0 ? (
              <div className="text-xs text-muted-foreground">
                Aucun document lié. Depuis le coffre-fort, vous pourrez rattacher factures, compromis, actes, devis, plans, permis, diagnostics, photos.
              </div>
            ) : (
              <ul className="text-sm space-y-1">
                {data.linked_documents.map((d) => (
                  <li key={d.id} className="flex justify-between">
                    <span>{d.title}</span>
                    <span className="text-xs text-muted-foreground">{d.doc_type}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl border border-dashed border-primary/30 bg-primary/5 p-3 text-xs text-primary/80 flex items-start gap-2">
            <Sparkles className="h-3.5 w-3.5 mt-0.5" />
            <div>
              TVA sur marge, simulations financières, OCR et rapprochement bancaire seront ajoutés dans les prochaines itérations sans casser cette architecture.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={remove} data-testid="mdb-delete">
            <Trash2 className="h-3 w-3 mr-1" />Supprimer
          </Button>
          <Button variant="outline" onClick={() => setOpenEdit(true)} data-testid="mdb-edit">Modifier</Button>
          <Button onClick={onClose}>Fermer</Button>
        </DialogFooter>
        {openEdit && <ProjectForm existing={data} onClose={() => setOpenEdit(false)} onSaved={() => { setOpenEdit(false); load(); }} />}
      </DialogContent>
    </Dialog>
  );
}
